import * as React from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow } from '@mui/material';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../app/store';
import reducerPath from '../../../../app/reducerPath';
import {
  LogisticTasksEnum,
  logisticTasksPetrolResourceActions,
  logisticTasksPetrolInitialState,
} from '../../../../entities/logistic/logisticTasks/redux/logisticTasksPetrol.slice';
import useDebounce from '../../../../shared/hooks/useDebounce';
import Header from './components/Header/Header';
import EnhancedTableHead from './components/EnhancedTableHead/EnhancedTableHead';
import VehicleRow from './components/VehicleRow/VehicleRow';
import DeleteVehicleDialog from './components/DeleteVehicleDialog/DeleteVehicleDialog';
import EnhancedTableToolbar from './components/EnhancedTableToolbar/EnhancedTableToolbar';

export default function PetrolDeliveryVehicleTableWidget() {
  const logisticTask = useAppSelector((state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`]);
  const { selectedVehicles, rePlanDate } = useAppSelector(
    (state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`],
  );

  const [searchVehicle, setSearchVehicle] = React.useState('');

  const sortedVehicles = React.useMemo(() => {
    const list = logisticTask?.data?.sourceWithSolution?.availableTankers || [];
    return [...list].sort((a, b) =>
      a.loadFactor === b.loadFactor ? a.id.localeCompare(b.id) : b.loadFactor - a.loadFactor,
    );
  }, [logisticTask?.data?.sourceWithSolution?.availableTankers]);

  // Фильтрация по поиску
  const vehicles = React.useMemo(() => {
    if (searchVehicle.length < 3) return sortedVehicles;

    const query = searchVehicle.toLowerCase();

    return sortedVehicles.filter((v) => {
      return [v.id, v.garage?.name, v.trailer?.registrationNum]
        .map((field) => (field ? String(field).toLowerCase() : ''))
        .some((field) => field.includes(query));
    });
  }, [sortedVehicles, searchVehicle]);

  const dispatch = useDispatch();

  const allErrorsLength = React.useMemo(
    () =>
      vehicles?.reduce((prev, veh) => {
        let sum = prev;
        const len = Number(veh?.statuses?.length);
        if (!Number.isNaN(len)) {
          sum += len;
        }
        return sum;
      }, 0),
    [vehicles],
  );

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('calories');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const [vehicleToDelete, setVehicleToDelete] = React.useState(null);
  const [deleteVehicleOrders, setDeleteVehicleOrders] = React.useState(false);

  const setDeleteVehicleOrdersCheck = () => {
    setDeleteVehicleOrders((prev) => !prev);
  };

  const deleteVehicle = () => {
    dispatch(
      logisticTasksPetrolResourceActions.removeVehicleFromTask({
        vehicleToDelete,
        deleteVehicleOrders,
      }),
    );
    setDeleteVehicleOrders(false);
    setVehicleToDelete(null);
  };

  const handleSelectAllClick = (event) => {
    let newSelected = [];
    if (event.target.checked) {
      newSelected = vehicles.map((n) => n.id);
    }
    dispatch(
      logisticTasksPetrolResourceActions.setData({
        [LogisticTasksEnum.selectedVehicles]: newSelected,
        [LogisticTasksEnum.selectedOrders]: [],
      }),
    );
  };

  const handleClick = (event, id) => {
    const selected = selectedVehicles;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    dispatch(
      logisticTasksPetrolResourceActions.setData({
        [LogisticTasksEnum.selectedVehicles]: newSelected,
        [LogisticTasksEnum.selectedOrders]: [],
      }),
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selectedVehicles.indexOf(id) !== -1;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - vehicles.length) : 0;

  // Task name state
  const [fastTaskName, setFastTaskName] = React.useState('');
  const onSetTaskNameDebounced = useDebounce((value) => {
    dispatch(logisticTasksPetrolResourceActions.setTaskName(value));
  }, 500);
  const onTaskNameChanged = (value) => {
    setFastTaskName(value);
    onSetTaskNameDebounced(value);
  };
  React.useEffect(() => {
    setFastTaskName(logisticTask?.data?.name || '');
  }, [logisticTask?.data?.name]);

  React.useEffect(
    () => () => dispatch(logisticTasksPetrolResourceActions.setData(logisticTasksPetrolInitialState)),
    [],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2, p: 2 }} elevation={1}>
        <Box sx={{ width: '100%' }}>
          <Header
            fastTaskName={fastTaskName}
            onTaskNameChanged={onTaskNameChanged}
            rePlanDate={rePlanDate}
            dispatch={dispatch}
          />
        </Box>
      </Paper>
      <Paper sx={{ width: '100%', mb: 2 }} elevation={1}>
        <EnhancedTableToolbar
          numSelected={selectedVehicles.length}
          allErrorsLength={allErrorsLength}
          onChangeSearchVehicle={setSearchVehicle}
        />
        <TableContainer>
          <Table sx={{ width: '100%' }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              numSelected={selectedVehicles.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={vehicles.length}
            />
            <TableBody>
              {(rowsPerPage > 0 ? vehicles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : vehicles)?.map(
                (row, index) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <VehicleRow
                      key={row.registrationNum}
                      row={row}
                      index={index}
                      isItemSelected={isItemSelected}
                      labelId={labelId}
                      handleClick={handleClick}
                      setVehicleToDelete={setVehicleToDelete}
                    />
                  );
                },
              )}
              {emptyRows > 0 && (
                <TableRow style={{ height: 33 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[15, 50, 100]}
          component="div"
          count={vehicles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <DeleteVehicleDialog
        open={vehicleToDelete}
        onSave={deleteVehicle}
        handleClose={() => {
          setDeleteVehicleOrders(false);
          setVehicleToDelete(null);
        }}
        deleteVehicleOrders={deleteVehicleOrders}
        setDeleteVehicleOrdersCheck={setDeleteVehicleOrdersCheck}
        vehicleToDelete={vehicleToDelete}
      />
    </Box>
  );
}
